import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { formatDate } from '../utils/dateFormat';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  Clock,
  Bell,
  Volume2,
  VolumeX
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format time to Eastern Time (ET)
const formatTimeToET = (timeString) => {
  if (!timeString) return '';
  
  try {
    // If it's just a time string (HH:MM:SS), create a full date in UTC for today
    const today = new Date().toISOString().split('T')[0];
    const fullDateTime = timeString.includes('T') ? timeString : `${today}T${timeString}Z`;
    
    const date = new Date(fullDateTime);
    
    return date.toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (e) {
    // Fallback: just show the raw time
    return timeString?.substring(0, 5) || '';
  }
};

// Helper to get current time in Eastern Time
const getCurrentTimeET = () => {
  return new Date().toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

// Helper to get current date in Eastern Time
const getCurrentDateET = () => {
  return new Date().toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const FoodPantryLobbyDisplayCharlottePage = () => {
  const [queue, setQueue] = useState([]);
  const [nowServing, setNowServing] = useState(null);
  const [charlotteLobbyQueue, setCharlotteLobbyQueue] = useState([]);
  const [alertDurationSeconds, setAlertDurationSeconds] = useState(60);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const [lastAnnouncedId, setLastAnnouncedId] = useState(null);
  const [lastAnnouncedTimestamp, setLastAnnouncedTimestamp] = useState(null);
  const [lastStaffAlertId, setLastStaffAlertId] = useState(null);
  const [showAudioPrompt, setShowAudioPrompt] = useState(false);
  const [autoInitAttempted, setAutoInitAttempted] = useState(false);
  
  const audioContextRef = useRef(null);
  const keepAliveIntervalRef = useRef(null);
  const autoInitTimeoutRef = useRef(null);

  // Initialize audio context - can be called automatically or manually
  const initializeAudio = useCallback(async (isManualTrigger = false) => {
    try {
      // Create AudioContext if not exists
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      // Resume audio context if suspended with timeout
      if (audioContextRef.current.state === 'suspended') {
        try {
          // Add timeout to prevent hanging
          await Promise.race([
            audioContextRef.current.resume(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Resume timeout')), 2000))
          ]);
        } catch (resumeError) {
          console.log('Resume failed or timed out:', resumeError.message);
        }
      }
      
      // Check if AudioContext is now running
      if (audioContextRef.current.state !== 'running') {
        console.log('AudioContext not running, state:', audioContextRef.current.state);
        if (!isManualTrigger) {
          return false; // Auto-init failed, need manual trigger
        }
      }
      
      // Play a test tone to confirm audio is working
      const osc = audioContextRef.current.createOscillator();
      const gain = audioContextRef.current.createGain();
      osc.connect(gain);
      gain.connect(audioContextRef.current.destination);
      osc.frequency.value = 440;
      gain.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.3);
      osc.start(audioContextRef.current.currentTime);
      osc.stop(audioContextRef.current.currentTime + 0.3);
      
      // Start Chrome keep-alive for speechSynthesis
      // This prevents Chrome from suspending the speech synthesis on always-on displays
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
      }
      keepAliveIntervalRef.current = setInterval(() => {
        if (window.speechSynthesis) {
          window.speechSynthesis.pause();
          window.speechSynthesis.resume();
        }
      }, 10000); // Every 10 seconds
      
      setAudioEnabled(true);
      setShowAudioPrompt(false);
      console.log('Audio initialized successfully');
      return true;
      
    } catch (e) {
      console.error('Failed to initialize audio:', e);
      if (!isManualTrigger) {
        return false; // Auto-init failed
      }
      return false;
    }
  }, []);

  // Manual enable audio (called from button click)
  const enableAudio = useCallback(() => {
    initializeAudio(true);
  }, [initializeAudio]);

  // Attempt automatic audio initialization on mount
  useEffect(() => {
    let mounted = true;
    
    const attemptAutoInit = async () => {
      // Small delay to ensure DOM is ready
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (!mounted) return;
      
      console.log('Attempting automatic audio initialization...');
      const success = await initializeAudio(false);
      
      if (!mounted) return;
      
      setAutoInitAttempted(true);
      
      if (!success) {
        console.log('Automatic audio init failed, showing manual prompt');
        setShowAudioPrompt(true);
      } else {
        console.log('Automatic audio init succeeded');
      }
    };
    
    // Attempt auto-init
    attemptAutoInit();
    
    return () => {
      mounted = false;
      if (autoInitTimeoutRef.current) {
        clearTimeout(autoInitTimeoutRef.current);
      }
    };
  }, [initializeAudio]); // Run once on mount

  // Cleanup keep-alive on unmount
  useEffect(() => {
    return () => {
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
      }
    };
  }, []);

  // Periodic check to reinitialize audio if it becomes suspended
  useEffect(() => {
    if (!audioEnabled) return;
    
    const checkAudioState = setInterval(async () => {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        console.log('AudioContext suspended, attempting to resume...');
        try {
          await audioContextRef.current.resume();
          if (audioContextRef.current.state === 'running') {
            console.log('AudioContext resumed successfully');
          }
        } catch (e) {
          console.error('Failed to resume AudioContext:', e);
        }
      }
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(checkAudioState);
  }, [audioEnabled]);

  // Play loud bell/chime sound
  const playAlertChime = useCallback(() => {
    if (!audioEnabled || !audioContextRef.current) return;
    
    try {
      const ctx = audioContextRef.current;
      const now = ctx.currentTime;
      
      // Create a louder, more attention-getting bell sound
      const playBellTone = (frequency, startTime, duration, volume = 0.8) => {
        const osc = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gain = ctx.createGain();
        const gain2 = ctx.createGain();
        
        // Main tone
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = frequency;
        osc.type = 'sine';
        
        // Harmonic overtone for richer sound
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.frequency.value = frequency * 2.5;
        osc2.type = 'sine';
        
        // Bell-like envelope with higher volume
        gain.gain.setValueAtTime(volume, startTime);
        gain.gain.exponentialRampToValueAtTime(volume * 0.7, startTime + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        gain2.gain.setValueAtTime(volume * 0.3, startTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, startTime + duration * 0.7);
        
        osc.start(startTime);
        osc.stop(startTime + duration);
        osc2.start(startTime);
        osc2.stop(startTime + duration);
      };
      
      // Play attention-getting bell pattern (louder, longer)
      playBellTone(830, now, 0.5, 0.9);           // High G#
      playBellTone(622, now + 0.15, 0.5, 0.9);    // D#
      playBellTone(830, now + 0.3, 0.6, 0.9);     // High G#
      playBellTone(1046, now + 0.5, 0.8, 1.0);    // High C - finale
      
    } catch (e) {
      console.error('Failed to play chime:', e);
    }
  }, [audioEnabled]);

  // Get the best available voice
  const getBestVoice = useCallback(() => {
    if (!('speechSynthesis' in window)) return null;
    
    // Re-query voices at speak time (important for Chrome)
    const voices = window.speechSynthesis.getVoices();
    
    if (!voices || voices.length === 0) {
      console.log('No voices available');
      return null;
    }
    
    // Priority order for voice selection
    const preferredVoice = 
      voices.find(v => v.lang === 'en-US' && v.name.includes('Google')) ||
      voices.find(v => v.lang === 'en-US' && v.name.includes('Microsoft')) ||
      voices.find(v => v.lang === 'en-US' && v.name.includes('Samantha')) ||
      voices.find(v => v.lang === 'en-US' && !v.localService) ||
      voices.find(v => v.lang === 'en-US') ||
      voices.find(v => v.lang.startsWith('en')) ||
      voices[0]; // Fallback to first available voice
    
    return preferredVoice;
  }, []);

  // Play voice announcement using Web Speech API
  const playVoiceAnnouncement = useCallback((text) => {
    if (!audioEnabled || !('speechSynthesis' in window)) {
      console.log('Speech synthesis not available or audio not enabled');
      return;
    }
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    // Get voice at speak time (re-query for Chrome reliability)
    const voice = getBestVoice();
    if (voice) {
      utterance.voice = voice;
      console.log('Using voice:', voice.name);
    } else {
      console.log('Using default voice');
    }
    
    utterance.onerror = (e) => {
      console.error('Speech synthesis error:', e);
    };
    
    utterance.onend = () => {
      console.log('Speech finished');
    };
    
    // Speak immediately (no delay)
    window.speechSynthesis.speak(utterance);
    
  }, [audioEnabled, getBestVoice]);

  // Handle announcement when now_serving changes or has play_announcement flag
  const handleAnnouncement = useCallback((serving) => {
    if (!serving || !audioEnabled) return;
    
    // Check if this is a new announcement (different id or timestamp)
    const shouldAnnounce = 
      serving.play_announcement && 
      (serving.id !== lastAnnouncedId || serving.timestamp !== lastAnnouncedTimestamp);
    
    if (shouldAnnounce) {
      console.log('Playing announcement for #' + serving.queue_number);
      
      // Play chime first
      playAlertChime();
      
      // Then play voice announcement after chime (shorter delay)
      const announcementText = serving.announcement_text || 
        `Attention. Pantry participant number ${serving.queue_number}, please meet us at the door to load your box into your vehicle.`;
      
      setTimeout(() => {
        playVoiceAnnouncement(announcementText);
      }, 800); // Reduced delay - just enough for chime
      
      // Update tracking to prevent duplicate announcements
      setLastAnnouncedId(serving.id);
      setLastAnnouncedTimestamp(serving.timestamp);
    }
  }, [audioEnabled, lastAnnouncedId, lastAnnouncedTimestamp, playAlertChime, playVoiceAnnouncement]);

  // Handle staff alert when new person is added to Charlotte lobby queue
  const handleStaffAlert = useCallback((entry) => {
    if (!entry || !audioEnabled) return;
    
    // Check if this is a new entry we haven't announced
    const shouldAnnounce = 
      entry.play_announcement && 
      entry.id !== lastStaffAlertId;
    
    if (shouldAnnounce) {
      console.log('Playing STAFF ALERT for #' + entry.queue_number);
      
      // Play chime first
      playAlertChime();
      
      // Then play staff alert message
      const staffAlertText = entry.announcement_text || 
        "Attention Pantry Staff, you have a shopper coming to the pantry";
      
      setTimeout(() => {
        playVoiceAnnouncement(staffAlertText);
      }, 800);
      
      // Update tracking to prevent duplicate announcements
      setLastStaffAlertId(entry.id);
    }
  }, [audioEnabled, lastStaffAlertId, playAlertChime, playVoiceAnnouncement]);

  const fetchQueue = useCallback(async () => {
    try {
      // For Charlotte, we only fetch from the Charlotte lobby queue (called participants)
      // NOT from the regular check-in queue - participants only appear after being called
      
      // Fetch main lobby display data (for now_serving alert and settings only)
      const response = await axios.get(`${API}/food-pantry/lobby-display/fp-charlotte`);
      // DON'T set queue from regular check-ins - Charlotte only shows called participants
      // setQueue(response.data.queue || []);
      
      if (response.data.now_serving_alert_seconds) {
        setAlertDurationSeconds(response.data.now_serving_alert_seconds);
      }
      
      const newNowServing = response.data.now_serving || null;
      
      if (newNowServing) {
        // Handle announcement if needed
        handleAnnouncement(newNowServing);
        
        setNowServing(prev => {
          if (!prev || prev.queue_number !== newNowServing.queue_number || prev.timestamp !== newNowServing.timestamp) {
            return newNowServing;
          }
          return prev;
        });
      } else {
        setNowServing(null);
      }
      
      // Fetch Charlotte lobby queue - this is the ONLY source for displayed participants
      // Participants appear here only after staff presses Call button
      try {
        const lobbyResponse = await axios.get(`${API}/food-pantry/charlotte-lobby-queue`);
        const lobbyEntries = lobbyResponse.data.queue || [];
        setCharlotteLobbyQueue(lobbyEntries);
        
        // Use Charlotte lobby queue as the display queue (only called participants)
        setQueue(lobbyEntries);
        
        // Check for new entries that need staff alert
        if (lobbyEntries.length > 0) {
          // Find newest entry that has play_announcement flag
          const newEntry = lobbyEntries.find(e => e.play_announcement && e.id !== lastStaffAlertId);
          if (newEntry) {
            handleStaffAlert(newEntry);
          }
        }
      } catch (lobbyError) {
        console.error('Failed to fetch Charlotte lobby queue:', lobbyError);
        setQueue([]); // Clear queue if fetch fails
      }
    } catch (error) {
      console.error('Failed to fetch queue:', error);
    } finally {
      setLoading(false);
    }
  }, [handleAnnouncement, handleStaffAlert, lastStaffAlertId]);

  // Load voices on mount and handle onvoiceschanged for Chrome
  useEffect(() => {
    if ('speechSynthesis' in window) {
      // Initial load attempt
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        setVoicesLoaded(true);
        console.log('Voices loaded initially:', voices.length);
      }
      
      // Chrome loads voices asynchronously
      window.speechSynthesis.onvoiceschanged = () => {
        const loadedVoices = window.speechSynthesis.getVoices();
        setVoicesLoaded(loadedVoices.length > 0);
        console.log('Voices loaded via onvoiceschanged:', loadedVoices.length);
      };
    }
  }, []);

  // Initial fetch and auto-refresh every 3 seconds for real-time updates
  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 3000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  // Auto-dismiss "Now Serving" alert based on configured duration
  useEffect(() => {
    if (nowServing) {
      const dismissTimer = setTimeout(() => {
        setNowServing(null);
      }, alertDurationSeconds * 1000);
      
      return () => clearTimeout(dismissTimer);
    }
  }, [nowServing, alertDurationSeconds]);

  // Update clock every second
  useEffect(() => {
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(clockInterval);
  }, []);

  const getStatusDisplay = (item) => {
    const status = item.queue_status || item.eligibility_status || 'In Review';
    
    if (status.includes('Eligible') || status.includes('Created')) {
      return { text: 'Ready', color: 'bg-green-500', textColor: 'text-green-500' };
    }
    if (status.includes('Waiting')) {
      return { text: 'Waiting', color: 'bg-blue-500', textColor: 'text-blue-500' };
    }
    if (status.includes('Completed')) {
      return { text: 'Completed', color: 'bg-gray-400', textColor: 'text-gray-400' };
    }
    if (status.includes('New Client')) {
      return { text: 'New Client', color: 'bg-purple-500', textColor: 'text-purple-500' };
    }
    if (status === 'Now Serving') {
      return { text: 'Now Serving', color: 'bg-green-500', textColor: 'text-green-500' };
    }
    if (status === 'Skipped') {
      return { text: 'Skipped', color: 'bg-orange-500', textColor: 'text-orange-500' };
    }
    return { text: 'In Review', color: 'bg-yellow-500', textColor: 'text-yellow-500' };
  };

  const formatName = (firstName, lastName) => {
    const initial = firstName ? firstName.charAt(0).toUpperCase() : '?';
    const last = lastName || 'Unknown';
    return `${initial}. ${last}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(302,58%,20%)] via-[hsl(302,58%,25%)] to-[hsl(302,58%,20%)] flex flex-col" data-testid="lobby-display-charlotte-page">
      {/* Audio Enable Button (shown only when automatic initialization fails) */}
      {showAudioPrompt && !audioEnabled && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-8 max-w-md text-center shadow-2xl">
            <Volume2 className="h-16 w-16 mx-auto mb-4 text-primary" />
            <h2 className="text-2xl font-bold mb-2">Enable Queue Audio</h2>
            <p className="text-gray-600 mb-6">
              Automatic audio initialization was blocked by browser security. Click below to enable voice announcements and alert sounds.
            </p>
            <Button size="lg" onClick={enableAudio} className="w-full text-lg py-6">
              <Volume2 className="h-6 w-6 mr-2" />
              Enable Queue Audio
            </Button>
            <p className="text-xs text-gray-400 mt-4">
              Tip: Configure Chrome with --autoplay-policy=no-user-gesture-required for automatic audio
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="p-6 text-center text-white border-b border-white/10">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <img 
              src="/myhelp-logo.png" 
              alt="MyHELP" 
              className="h-12 w-auto brightness-0 invert"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="text-left">
              <h1 className="text-2xl md:text-3xl font-bold">Charlotte Food Pantry</h1>
              <p className="text-purple-200 text-sm md:text-base">Client Check-In Queue</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Audio indicator */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded ${
              audioEnabled 
                ? 'bg-green-600/30' 
                : autoInitAttempted && !showAudioPrompt 
                  ? 'bg-yellow-600/30' 
                  : 'bg-red-600/30'
            }`}>
              {audioEnabled ? (
                <>
                  <Volume2 className="h-4 w-4" />
                  <span className="text-xs">Audio On</span>
                </>
              ) : autoInitAttempted && !showAudioPrompt ? (
                <>
                  <Volume2 className="h-4 w-4 animate-pulse" />
                  <span className="text-xs">Initializing...</span>
                </>
              ) : (
                <>
                  <VolumeX className="h-4 w-4" />
                  <span className="text-xs">Audio Off</span>
                </>
              )}
            </div>
            <div className="text-right">
              <p className="text-3xl md:text-4xl font-bold font-mono">
                {getCurrentTimeET()}
              </p>
              <p className="text-purple-200 text-sm">
                {getCurrentDateET()} (ET)
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Now Serving Alert */}
      {nowServing && (
        <div className="bg-gradient-to-r from-green-600 to-green-500 text-white p-6 md:p-8 animate-pulse">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-6">
            <Bell className="h-12 w-12 md:h-16 md:w-16 animate-bounce" />
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold mb-2">NOW SERVING</p>
              <p className="text-5xl md:text-7xl font-bold mb-4">#{nowServing.queue_number}</p>
              <p className="text-xl md:text-2xl">{nowServing.message || "Please meet us at the door to have assistance taking your box to your car"}</p>
            </div>
            <Bell className="h-12 w-12 md:h-16 md:w-16 animate-bounce" />
          </div>
        </div>
      )}

      {/* Main Queue Display */}
      <main className="flex-1 p-6 md:p-8 overflow-hidden">
        <div className="max-w-7xl mx-auto h-full">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-white text-2xl">Loading queue...</div>
            </div>
          ) : queue.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-white">
              <Users className="h-20 w-20 mb-6 opacity-50" />
              <p className="text-2xl md:text-3xl font-medium">No clients in queue</p>
              <p className="text-purple-200 text-lg mt-2">Check in at the kiosk to get started</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {/* Queue Header */}
              <div className="grid grid-cols-12 gap-4 px-6 py-3 text-purple-200 text-lg font-medium border-b border-white/20">
                <div className="col-span-2 text-center">#</div>
                <div className="col-span-5">Name</div>
                <div className="col-span-3">Time</div>
                <div className="col-span-2 text-center">Status</div>
              </div>
              
              {/* Queue Items */}
              <div className="space-y-3 overflow-y-auto max-h-[calc(100vh-350px)]">
                {queue.map((item, index) => {
                  const status = getStatusDisplay(item);
                  const isNowServing = nowServing && nowServing.queue_number === item.queue_number;
                  
                  return (
                    <Card 
                      key={item.id} 
                      className={`border-0 shadow-lg transition-all duration-300 ${
                        isNowServing 
                          ? 'bg-green-100 ring-4 ring-green-500 scale-[1.02]' 
                          : 'bg-white/95 hover:bg-white'
                      }`}
                    >
                      <CardContent className="p-4 md:p-6">
                        <div className="grid grid-cols-12 gap-4 items-center">
                          {/* Queue Number */}
                          <div className="col-span-2 text-center">
                            <span className={`text-3xl md:text-4xl font-bold ${
                              isNowServing ? 'text-green-600' : 'text-primary'
                            }`}>
                              #{item.queue_number}
                            </span>
                          </div>
                          
                          {/* Name */}
                          <div className="col-span-5">
                            <p className={`text-2xl md:text-3xl font-semibold ${
                              isNowServing ? 'text-green-700' : 'text-gray-800'
                            }`}>
                              {formatName(item.first_name, item.last_name)}
                            </p>
                            {item.client_type === 'new' && (
                              <span className="text-sm text-purple-600 font-medium">New Client</span>
                            )}
                          </div>
                          
                          {/* Check-in/Called Time (ET) */}
                          <div className="col-span-3 flex items-center gap-2 text-gray-600">
                            <Clock className="h-5 w-5" />
                            <span className="text-xl md:text-2xl font-medium">
                              {formatTimeToET(item.called_at || item.checkin_time)}
                            </span>
                            <span className="text-sm text-gray-400">ET</span>
                          </div>
                          
                          {/* Status */}
                          <div className="col-span-2 text-center">
                            <Badge className={`${status.color} text-white text-lg md:text-xl px-4 py-2`}>
                              {status.text}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-blue-200 text-sm border-t border-white/10">
        <p>Please wait for your number to be called • Thank you for your patience</p>
      </footer>
    </div>
  );
};

export default FoodPantryLobbyDisplayCharlottePage;
